import type { useTexts } from '../../useTexts'
import type { BalanceBy, DriverUsage, OptimizationStrategy } from '../../hooks/useController'

export type SampleBar = { letter: string; value: number | null }

export type StrategyVariant = {
  bars: SampleBar[]
  barColor: 'success' | 'info'
  outcome: {
    vehicleUsage: string
    travelEfficiency: string
    workloadFairness: string
    loadSaturation: string
  }
}

type Texts = ReturnType<typeof useTexts>

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F']

const bars = (values: (number | null)[]): SampleBar[] =>
  LETTERS.map((letter, i) => ({ letter, value: values[i] ?? null }))

/**
 * Returns the static example data for the currently selected combination of
 * strategy + driver usage + balance type. Six variants per the FE Figma:
 *
 *   1. Fleet savings  + Minimize drivers
 *   2. Fleet savings  + Use all active drivers
 *   3. Balanced + Minimize drivers + (Orders | WorkingTime | DrivingTime)
 *   4. Balanced + Minimize drivers + LoadType
 *   5. Balanced + Use all active   + (Orders | WorkingTime | DrivingTime)
 *   6. Balanced + Use all active   + LoadType
 *
 * Values are illustrative (per the disclaimer in the panel) and do not come
 * from the optimizer — they mimic the visual signature of each strategy.
 */
export function getStrategyVariant(
  strategy: OptimizationStrategy,
  driverUsage: DriverUsage,
  balanceBy: BalanceBy | '',
  texts: Texts,
): StrategyVariant {
  if (strategy === 'fleetSavings') {
    if (driverUsage === 'minimizeDrivers') {
      return {
        bars: bars([95, 85, 80, null, null, null]),
        barColor: 'success',
        outcome: {
          vehicleUsage: texts.outcomeMin,
          travelEfficiency: texts.outcomeMax,
          workloadFairness: texts.outcomeLow,
          loadSaturation: texts.outcomeHigh,
        },
      }
    }
    // Use all active drivers — spread work unevenly across the full fleet.
    return {
      bars: bars([80, 50, 70, 20, 40, 80]),
      barColor: 'success',
      outcome: {
        vehicleUsage: texts.outcomeMax,
        travelEfficiency: texts.outcomeMedium,
        workloadFairness: texts.outcomeLow,
        loadSaturation: texts.outcomeLow,
      },
    }
  }

  // Balanced performance
  const isLoad = balanceBy === 'loadType'

  if (driverUsage === 'minimizeDrivers') {
    return {
      bars: bars([70, 70, 70, 70, null, null]),
      barColor: 'info',
      outcome: {
        vehicleUsage: texts.outcomeMin,
        travelEfficiency: isLoad ? texts.outcomeLow : texts.outcomeMedium,
        workloadFairness: texts.outcomeMax,
        loadSaturation: isLoad ? texts.outcomeMax : texts.outcomeHigh,
      },
    }
  }

  // Use all active drivers + Balanced
  return {
    bars: bars([50, 50, 50, 50, 50, 50]),
    barColor: 'info',
    outcome: {
      vehicleUsage: texts.outcomeMax,
      travelEfficiency: isLoad ? texts.outcomeMin : texts.outcomeLow,
      workloadFairness: texts.outcomeMax,
      loadSaturation: isLoad ? texts.outcomeMedium : texts.outcomeLow,
    },
  }
}
